import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAdaContext, AdaContextValue } from './contexts/AdaContext';

// Action tool names that the backend won't execute — instead they get
// streamed back here as an action proposal, and the user has to Apply
// before they take effect. Must match ACTION_TOOL_NAMES in
// backend/services/ada/tools/index.js.
const ACTION_TOOL_NAMES = new Set([
  'apply_filter_groups',
  'switch_tab',
  'apply_radius_search',
  'set_keyword_search',
  'set_employment_types',
  'propose_smart_search',
]);

type AggregateRow = { value: string; count: number };
type ToolUse = { id: string; name: string; input: any; isServer?: boolean };
type ToolResult = { result: any; is_error?: boolean; isWebSearch?: boolean };

// One item from web_search_tool_result.content — the shape Anthropic returns
// for hosted web search. Only the fields we render are typed; the rest pass
// through opaquely.
type WebSearchResult = {
  type: 'web_search_result';
  url: string;
  title: string;
  encrypted_content?: string;
  page_age?: string | null;
};

// Pending action proposal: a set of action tool_uses Claude emitted that
// the user hasn't yet accepted or declined. While `status === 'pending'`
// the chat shows the ActionCard; on apply/discard we send a continuation
// request and the status moves to 'applying' then 'applied' / 'discarded'.
//
// stepStatuses tracks each individual action's progress so the card can
// narrate the sequence (running → done) rather than collapsing instantly.
// This is the "show the user what's happening" educational mode that
// turns Ada from a black-box agent into a visible tutor.
type StepStatus = 'queued' | 'running' | 'done' | 'error';
type PendingActions = {
  preText: string;
  toolUses: ToolUse[];
  status: 'pending' | 'applying' | 'applied' | 'discarded';
  stepStatuses?: Record<string, StepStatus>;
  errors?: Record<string, string>;
  // ID of the assistant row the backend created to "hold" this proposal.
  // Sent back with the continuation so the backend UPDATEs that row in
  // place rather than inserting a duplicate. Without it, the proposal text
  // would appear twice in conversation history.
  proposalMsgId?: number;
};

type ContextSnapshot = {
  dataType: AdaContextValue['dataType'];
  projectName: string | null;
  associatedCompany: string | null;
  visibleTotal: number | null;
  filterCount: number;
  searchTerm: string | null;
  employmentTypeCount: number;
  radiusActive: boolean;
};

type Message = {
  role: 'user' | 'assistant';
  content: string;
  toolUses: ToolUse[];
  toolResults: Record<string, ToolResult>;
  context?: ContextSnapshot;
  // Set on assistant messages when Claude proposed action tools that
  // need user approval before execution. While pending, the message
  // renders an ActionCard instead of letting the action tool_uses
  // appear as regular ToolCards.
  pendingActions?: PendingActions;
};

type Conversation = {
  id: number;
  title: string;
  created_at: string;
  updated_at: string;
  last_message_at: string | null;
  message_count: number;
};

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
  'Content-Type': 'application/json',
});

function snapshot(ctx: AdaContextValue): ContextSnapshot {
  return {
    dataType: ctx.dataType,
    projectName: ctx.projectName,
    associatedCompany: ctx.associatedCompany,
    visibleTotal: ctx.visibleTotal,
    filterCount: (ctx.filterGroups || []).length,
    searchTerm: ctx.searchTerm,
    employmentTypeCount: (ctx.employmentTypes || []).length,
    radiusActive: !!ctx.radius && Object.keys(ctx.radius).length > 0,
  };
}

function sameContext(a?: ContextSnapshot, b?: ContextSnapshot): boolean {
  if (!a || !b) return false;
  return (
    a.dataType === b.dataType &&
    a.projectName === b.projectName &&
    a.associatedCompany === b.associatedCompany &&
    a.filterCount === b.filterCount &&
    a.searchTerm === b.searchTerm &&
    a.employmentTypeCount === b.employmentTypeCount &&
    a.radiusActive === b.radiusActive
  );
}

function describeContext(c: ContextSnapshot): string {
  const parts: string[] = [];
  if (c.dataType) parts.push(c.dataType);
  if (c.projectName) parts.push(`in ${c.projectName}`);
  if (c.associatedCompany) parts.push(`at ${c.associatedCompany}`);
  if (c.searchTerm) parts.push(`"${c.searchTerm}"`);
  if (c.filterCount > 0) parts.push(`+${c.filterCount} filter${c.filterCount > 1 ? 's' : ''}`);
  if (c.employmentTypeCount > 0) parts.push(`${c.employmentTypeCount} emp type${c.employmentTypeCount > 1 ? 's' : ''}`);
  if (c.radiusActive) parts.push('radius');
  if (c.visibleTotal != null) parts.push(`${c.visibleTotal.toLocaleString()} rows`);
  return parts.join(' · ');
}

// Bucket a conversation's last activity into a coarse age band so the menu
// can group "Today / This week / This month / Older". Keeps the dropdown
// scannable even with 50+ chats.
const TIME_BUCKETS = ['Today', 'This week', 'This month', 'Older'] as const;
type TimeBucket = typeof TIME_BUCKETS[number];

function bucketByAge(isoTimestamp: string | null | undefined): TimeBucket {
  if (!isoTimestamp) return 'Older';
  const ts = new Date(isoTimestamp).getTime();
  if (Number.isNaN(ts)) return 'Older';
  const now = Date.now();
  const ageMs = now - ts;
  const dayMs = 24 * 60 * 60 * 1000;
  // "Today" = same calendar day in the user's local TZ
  const today = new Date();
  const that = new Date(ts);
  if (today.toDateString() === that.toDateString()) return 'Today';
  if (ageMs < 7 * dayMs) return 'This week';
  if (ageMs < 31 * dayMs) return 'This month';
  return 'Older';
}

export default function AdaSidebar() {
  const ctx = useAdaContext();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<number | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [menuSearch, setMenuSearch] = useState('');
  // Inline rename state — id of the row being edited and its draft title.
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [renamingDraft, setRenamingDraft] = useState('');

  const threadRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight });
  }, [messages, streaming]);

  const inScope = !!(ctx.projectId && ctx.dataType);

  const projectId = ctx.projectId;

  // Fetch the conversation list for the currently-open project. Runs when
  // the project changes OR when the sidebar is first opened.
  const refreshConversations = useCallback(async (): Promise<Conversation[]> => {
    if (!projectId) return [];
    try {
      const r = await fetch(`/api/ada/conversations?projectId=${projectId}`, { headers: getAuthHeaders() });
      if (!r.ok) return [];
      const data = await r.json();
      const list = (data.conversations || []) as Conversation[];
      setConversations(list);
      return list;
    } catch {
      return [];
    }
  }, [projectId]);

  const loadConversation = useCallback(async (id: number) => {
    setLoadingConversation(true);
    setMessages([]);
    setActiveConversationId(id);
    try {
      const r = await fetch(`/api/ada/conversations/${id}`, { headers: getAuthHeaders() });
      if (!r.ok) return;
      const data = await r.json();
      const restored: Message[] = (data.messages || []).map((m: any) => ({
        role: m.role,
        content: m.content || '',
        toolUses: Array.isArray(m.tool_uses) ? m.tool_uses : [],
        toolResults: m.tool_results || {},
        context: m.context_snapshot
          ? {
              dataType: m.context_snapshot.dataType,
              projectName: ctx.projectName,
              associatedCompany: m.context_snapshot.associatedCompany || null,
              visibleTotal: null,
              filterCount: (m.context_snapshot.filterGroups || []).length,
            }
          : undefined,
      }));
      setMessages(restored);
    } finally {
      setLoadingConversation(false);
    }
  }, [ctx.projectName]);

  const createNewConversation = useCallback(async (): Promise<number | null> => {
    if (!projectId) return null;
    const r = await fetch('/api/ada/conversations', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ projectId }),
    });
    if (!r.ok) return null;
    const data = await r.json();
    const conv: Conversation = data.conversation;
    setConversations((prev) => [conv, ...prev]);
    setActiveConversationId(conv.id);
    setMessages([]);
    return conv.id;
  }, [projectId]);

  // When the sidebar opens (or the project changes while it's open), fetch
  // the conversation list and surface the most recent one. If there are no
  // conversations yet, start an empty state — we'll lazily create on send.
  useEffect(() => {
    if (!open || !projectId) return;
    let cancelled = false;
    (async () => {
      const list = await refreshConversations();
      if (cancelled) return;
      if (list.length > 0) {
        await loadConversation(list[0].id);
      } else {
        setActiveConversationId(null);
        setMessages([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, projectId, refreshConversations, loadConversation]);

  const handleNewChat = async () => {
    setMenuOpen(false);
    setInput('');
    await createNewConversation();
  };

  const handleSwitchConversation = async (id: number) => {
    setMenuOpen(false);
    if (id === activeConversationId) return;
    await loadConversation(id);
  };

  const startRenaming = (c: Conversation, e: React.MouseEvent) => {
    e.stopPropagation();
    setRenamingId(c.id);
    setRenamingDraft(c.title);
  };

  const cancelRenaming = () => {
    setRenamingId(null);
    setRenamingDraft('');
  };

  const commitRename = async (id: number) => {
    const next = renamingDraft.trim().slice(0, 200);
    cancelRenaming();
    if (!next) return;
    const original = conversations.find((c) => c.id === id)?.title;
    if (!original || next === original) return;
    // Optimistic update.
    setConversations((prev) => prev.map((c) => (c.id === id ? { ...c, title: next } : c)));
    try {
      const r = await fetch(`/api/ada/conversations/${id}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ title: next }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
    } catch {
      // Roll back if the server rejected the rename.
      setConversations((prev) => prev.map((c) => (c.id === id ? { ...c, title: original } : c)));
    }
  };

  const handleDeleteConversation = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Delete this conversation?')) return;
    const r = await fetch(`/api/ada/conversations/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
    if (!r.ok) return;
    setConversations((prev) => prev.filter((c) => c.id !== id));
    if (id === activeConversationId) {
      const remaining = conversations.filter((c) => c.id !== id);
      if (remaining.length > 0) {
        await loadConversation(remaining[0].id);
      } else {
        setActiveConversationId(null);
        setMessages([]);
      }
    }
  };

  const send = async () => {
    if (!input.trim() || !inScope || streaming) return;

    // Lazy-create a conversation on first send if none active.
    let convId = activeConversationId;
    if (!convId) {
      convId = await createNewConversation();
      if (!convId) return;
    }

    const ctxSnap = snapshot(ctx);
    const userMsg: Message = {
      role: 'user',
      content: input.trim(),
      toolUses: [],
      toolResults: {},
      context: ctxSnap,
    };
    const baseMessages: Message[] = [...messages, userMsg];
    const assistantIndex = baseMessages.length;

    setMessages([...baseMessages, { role: 'assistant', content: '', toolUses: [], toolResults: {} }]);
    setInput('');
    setStreaming(true);

    try {
      const response = await fetch('/api/ada/chat', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          message: userMsg.content,
          conversation_id: convId,
          context: {
            projectId: ctx.projectId,
            projectName: ctx.projectName,
            dataType: ctx.dataType,
            filterGroups: ctx.filterGroups,
            associatedCompany: ctx.associatedCompany,
            // Full filter state — backend's buildScopedWhereClause applies
            // any of these that are present.
            searchTerm: ctx.searchTerm,
            employmentTypes: ctx.employmentTypes,
            radius: ctx.radius,
          },
        }),
      });

      if (!response.ok || !response.body) {
        const errText = (await response.text().catch(() => '')) || `HTTP ${response.status}`;
        setMessages((prev) => {
          const next = [...prev];
          next[assistantIndex] = { ...next[assistantIndex], content: `(Error: ${errText})` };
          return next;
        });
        setStreaming(false);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let sep;
        while ((sep = buffer.indexOf('\n\n')) !== -1) {
          const chunk = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          if (!chunk.startsWith('data: ')) continue;
          let event: any;
          try { event = JSON.parse(chunk.slice(6)); } catch { continue; }

          if (event.type === 'title_update') {
            // Reflect the auto-generated title in the menu immediately.
            setConversations((prev) =>
              prev.map((c) => (c.id === event.conversation_id ? { ...c, title: event.title } : c))
            );
            continue;
          }

          setMessages((prev) => {
            const next = [...prev];
            const msg: Message = { ...next[assistantIndex] };
            msg.toolUses = [...msg.toolUses];
            msg.toolResults = { ...msg.toolResults };
            if (event.type === 'text') {
              msg.content += event.content || '';
            } else if (event.type === 'tool_use') {
              // Don't accumulate action tool_uses into the regular tool_uses
              // list — those will be re-emitted as part of an actions_pending
              // event right after the loop ends. Data tools accumulate as before.
              if (!ACTION_TOOL_NAMES.has(event.name)) {
                if (!msg.toolUses.find((t) => t.id === event.tool_use_id)) {
                  msg.toolUses.push({ id: event.tool_use_id, name: event.name, input: event.input });
                }
              }
            } else if (event.type === 'server_tool_use') {
              // Server tool (web search) — Anthropic ran it for us. Track it
              // in toolUses so the rendering pipeline can show a "Searched
              // the web" card.
              if (!msg.toolUses.find((t) => t.id === event.tool_use_id)) {
                msg.toolUses.push({
                  id: event.tool_use_id,
                  name: event.name,
                  input: event.input,
                  isServer: true,
                });
              }
            } else if (event.type === 'web_search_tool_result') {
              msg.toolResults[event.tool_use_id] = {
                result: event.content,
                isWebSearch: true,
              };
            } else if (event.type === 'tool_result') {
              msg.toolResults[event.tool_use_id] = { result: event.result, is_error: event.is_error };
            } else if (event.type === 'actions_pending') {
              // Claude proposed an action — show the ActionCard for user
              // approval. The text accumulated so far becomes the proposal's
              // pre-text. Clear msg.content so the card stands alone.
              msg.pendingActions = {
                preText: event.pre_text || msg.content || '',
                toolUses: (event.tool_uses || []).map((tu: any) => ({
                  id: tu.id, name: tu.name, input: tu.input,
                })),
                status: 'pending',
                proposalMsgId: typeof event.proposal_msg_id === 'number'
                  ? event.proposal_msg_id
                  : undefined,
              };
              msg.content = '';
            } else if (event.type === 'error') {
              msg.content = (msg.content ? msg.content + '\n\n' : '') + `(Error: ${event.message})`;
            }
            next[assistantIndex] = msg;
            return next;
          });
        }
      }
    } catch (err: any) {
      setMessages((prev) => {
        const next = [...prev];
        next[assistantIndex] = { ...next[assistantIndex], content: `(Error: ${err.message || err})` };
        return next;
      });
    } finally {
      setStreaming(false);
      // Bring the active conversation to the top of the list and refresh
      // last_message_at after the assistant turn finishes.
      refreshConversations();
    }
  };

  // -------------------------------------------------------------------------
  // Action proposals: Apply / Discard handlers
  // -------------------------------------------------------------------------

  // Dispatches an action and optionally returns a result object that the
  // continuation tool_result should carry back to Claude. Most actions just
  // return undefined and applyPendingActions defaults to `{applied: true}`;
  // propose_smart_search returns richer data ({applied, keywordsApplied,
  // error?}) so Ada can ack precisely.
  const dispatchAction = async (tu: ToolUse): Promise<any> => {
    const { name, input } = tu;
    if (name === 'apply_filter_groups') {
      ctx.actions.applyFilterGroups(input?.groups || [], input?.mode || 'replace');
      return;
    }
    if (name === 'switch_tab') {
      ctx.actions.switchTab(input?.tab);
      return;
    }
    if (name === 'apply_radius_search') {
      await ctx.actions.applyRadiusSearch({
        type: input?.type,
        location: input?.location,
        distance: input?.distance,
      });
      return;
    }
    if (name === 'set_keyword_search') {
      ctx.actions.setKeywordSearch(input?.term ?? '');
      return;
    }
    if (name === 'set_employment_types') {
      ctx.actions.setEmploymentTypes(Array.isArray(input?.types) ? input.types : []);
      return;
    }
    if (name === 'propose_smart_search') {
      return await ctx.actions.runSmartSearch(input?.query ?? '');
    }
    throw new Error(`Unknown action tool: ${name}`);
  };

  // Stream the continuation request back into the same assistant message —
  // Claude's follow-up text appears below the (now collapsed) action card.
  const streamContinuation = async (
    msgIndex: number,
    toolResults: Array<{ tool_use_id: string; content: string; is_error?: boolean }>,
    assistantPreText: string,
    assistantToolUses: ToolUse[],
    proposalMsgId?: number
  ) => {
    setStreaming(true);
    try {
      const response = await fetch('/api/ada/chat', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          conversation_id: activeConversationId,
          context: {
            projectId: ctx.projectId,
            projectName: ctx.projectName,
            dataType: ctx.dataType,
            filterGroups: ctx.filterGroups,
            associatedCompany: ctx.associatedCompany,
            searchTerm: ctx.searchTerm,
            employmentTypes: ctx.employmentTypes,
            radius: ctx.radius,
          },
          tool_results: toolResults,
          assistant_pre_text: assistantPreText,
          assistant_tool_uses: assistantToolUses,
          proposal_msg_id: proposalMsgId,
        }),
      });

      if (!response.ok || !response.body) {
        const errText = (await response.text().catch(() => '')) || `HTTP ${response.status}`;
        setMessages(prev => {
          const next = [...prev];
          const m = { ...next[msgIndex] };
          m.content = (m.content ? m.content + '\n\n' : '') + `(Error: ${errText})`;
          next[msgIndex] = m;
          return next;
        });
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let sep;
        while ((sep = buffer.indexOf('\n\n')) !== -1) {
          const chunk = buffer.slice(0, sep);
          buffer = buffer.slice(sep + 2);
          if (!chunk.startsWith('data: ')) continue;
          let event: any;
          try { event = JSON.parse(chunk.slice(6)); } catch { continue; }
          if (event.type === 'title_update') continue;
          setMessages(prev => {
            const next = [...prev];
            const m = { ...next[msgIndex] };
            if (event.type === 'text') {
              m.content = (m.content || '') + (event.content || '');
            } else if (event.type === 'error') {
              m.content = (m.content ? m.content + '\n\n' : '') + `(Error: ${event.message})`;
            }
            next[msgIndex] = m;
            return next;
          });
        }
      }
    } finally {
      setStreaming(false);
      refreshConversations();
    }
  };

  const applyPendingActions = async (msgIndex: number) => {
    const msg = messages[msgIndex];
    if (!msg?.pendingActions || msg.pendingActions.status !== 'pending') return;

    const { toolUses, preText } = msg.pendingActions;

    // NB: we deliberately do NOT setStreaming(true) here. The user must be
    // able to ask Ada about a suggested keyword (e.g. "what is PySpark?")
    // while the Smart Search preview modal is still open — that's the core
    // educational moment. The dual-user-message bug that used to bite here
    // is now prevented at the source: the proposal is persisted as an
    // assistant row when actions_pending streams (see backend), so any
    // follow-up user message has a proper assistant turn before it in
    // history.

    // Seed all step statuses to 'queued' and flip the card into 'applying'
    // mode. The card will narrate each step's progress as we go.
    const initialStatuses: Record<string, StepStatus> = {};
    for (const tu of toolUses) initialStatuses[tu.id] = 'queued';
    setMessages(prev => {
      const next = [...prev];
      if (next[msgIndex]?.pendingActions) {
        next[msgIndex] = {
          ...next[msgIndex],
          pendingActions: {
            ...next[msgIndex].pendingActions!,
            status: 'applying',
            stepStatuses: initialStatuses,
            errors: {},
          },
        };
      }
      return next;
    });

    const setStepStatus = (id: string, status: StepStatus, errorMsg?: string) => {
      setMessages(prev => {
        const next = [...prev];
        const m = next[msgIndex];
        if (!m?.pendingActions) return prev;
        const stepStatuses = { ...(m.pendingActions.stepStatuses || {}), [id]: status };
        const errors = errorMsg
          ? { ...(m.pendingActions.errors || {}), [id]: errorMsg }
          : m.pendingActions.errors;
        next[msgIndex] = {
          ...m,
          pendingActions: { ...m.pendingActions, stepStatuses, errors },
        };
        return next;
      });
    };

    // Dispatch each action sequentially, narrating the transition queued →
    // running → done. The small delay between steps is deliberate: a sub-
    // second pause is what makes the sequence feel like "Ada is doing
    // things" rather than a single instant state change. Without it the
    // educational value of the visible narration is lost.
    const results: Array<{ tool_use_id: string; content: string; is_error?: boolean }> = [];
    for (let i = 0; i < toolUses.length; i++) {
      const tu = toolUses[i];
      setStepStatus(tu.id, 'running');
      try {
        const dispatched = await dispatchAction(tu);
        // Hold on "running" briefly so the user perceives the step. Skip
        // the artificial delay for propose_smart_search because the modal
        // already provides a long enough perceptual pause.
        if (tu.name !== 'propose_smart_search') {
          await new Promise(r => setTimeout(r, 350));
        }
        // Pass the dispatcher's result (if any) through to Claude.
        // Defaults to `{applied: true}` for actions that don't return one.
        const payload = dispatched ?? { applied: true };
        setStepStatus(tu.id, payload.applied === false ? 'error' : 'done', payload.applied === false ? (payload.error || 'cancelled') : undefined);
        results.push({
          tool_use_id: tu.id,
          content: JSON.stringify(payload),
        });
      } catch (err: any) {
        const errMsg = err?.message || String(err);
        setStepStatus(tu.id, 'error', errMsg);
        results.push({
          tool_use_id: tu.id,
          content: JSON.stringify({ applied: false, error: errMsg }),
          is_error: true,
        });
      }
      // Inter-step breather (skip after the last action).
      if (i < toolUses.length - 1) {
        await new Promise(r => setTimeout(r, 250));
      }
    }

    // Mark applied — the card collapses to a confirmation list.
    setMessages(prev => {
      const next = [...prev];
      if (next[msgIndex]?.pendingActions) {
        next[msgIndex] = {
          ...next[msgIndex],
          pendingActions: { ...next[msgIndex].pendingActions!, status: 'applied' },
        };
      }
      return next;
    });

    // Now ask Claude for the follow-up text. The continuation streams into
    // the same message (Claude says something like "Done — try refining by…").
    await streamContinuation(msgIndex, results, preText, toolUses, msg.pendingActions.proposalMsgId);
  };

  const discardPendingActions = async (msgIndex: number) => {
    const msg = messages[msgIndex];
    if (!msg?.pendingActions || msg.pendingActions.status !== 'pending') return;

    const { toolUses, preText, proposalMsgId } = msg.pendingActions;

    setMessages(prev => {
      const next = [...prev];
      if (next[msgIndex]?.pendingActions) {
        next[msgIndex] = {
          ...next[msgIndex],
          pendingActions: { ...next[msgIndex].pendingActions!, status: 'discarded' },
        };
      }
      return next;
    });

    const results = toolUses.map(tu => ({
      tool_use_id: tu.id,
      content: JSON.stringify({ applied: false, declined: true }),
    }));

    await streamContinuation(msgIndex, results, preText, toolUses, proposalMsgId);
  };

  const renderToolResult = (toolName: string, result: any) => {
    // Web search results: Anthropic returns an array of {type, url, title, ...}
    // entries — render as a compact citation list so the user sees where Ada
    // got its info from.
    if (toolName === 'web_search' && Array.isArray(result)) {
      const hits = result.filter((r: any) => r?.type === 'web_search_result') as WebSearchResult[];
      if (hits.length === 0) {
        return <div className="mt-2 text-[11px] text-gray-400 italic">No results.</div>;
      }
      return (
        <ul className="mt-2 space-y-1.5">
          {hits.slice(0, 6).map((hit, i) => {
            let host = '';
            try { host = new URL(hit.url).host.replace(/^www\./, ''); } catch {}
            return (
              <li key={i} className="text-[11px]">
                <a
                  href={hit.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-700 hover:underline font-medium"
                >
                  {hit.title || hit.url}
                </a>
                {host && <span className="ml-1 text-gray-400">· {host}</span>}
              </li>
            );
          })}
          {hits.length > 6 && (
            <li className="text-[10px] text-gray-400">+ {hits.length - 6} more</li>
          )}
        </ul>
      );
    }
    if (toolName === 'aggregate_field' && result && Array.isArray(result.rows)) {
      const max = Math.max(...result.rows.map((r: AggregateRow) => r.count), 1);
      return (
        <div className="mt-2">
          <table className="w-full text-xs">
            <tbody>
              {result.rows.map((row: AggregateRow, i: number) => (
                <tr key={i}>
                  <td className="py-0.5 pr-2 text-gray-700 truncate max-w-[200px]" title={row.value}>
                    {row.value ?? '(empty)'}
                  </td>
                  <td className="py-0.5 pr-2 text-right text-gray-500 tabular-nums">{row.count}</td>
                  <td className="py-0.5 w-16">
                    <div className="h-1.5 rounded bg-purple-200 overflow-hidden">
                      <div className="h-full bg-purple-600" style={{ width: `${(row.count / max) * 100}%` }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {typeof result.total_rows_considered === 'number' && (
            <div className="mt-1 text-[10px] text-gray-400">
              Based on {result.total_rows_considered.toLocaleString()} {result.unit || 'rows'}.
            </div>
          )}
        </div>
      );
    }
    return (
      <pre className="mt-2 text-[10px] text-gray-600 whitespace-pre-wrap break-all">
        {JSON.stringify(result, null, 2)}
      </pre>
    );
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed right-0 top-1/2 -translate-y-1/2 z-[60] bg-purple-600 text-white text-xs py-3 px-2 rounded-l-md shadow-lg hover:bg-purple-700 transition"
        title="Open Ada"
      >
        <span className="block [writing-mode:vertical-rl] rotate-180">✦ Ada</span>
      </button>
    );
  }

  const activeConv = conversations.find((c) => c.id === activeConversationId);

  return (
    <aside className="fixed right-0 top-0 bottom-0 w-[380px] max-md:w-full z-[60] flex flex-col bg-white border-l border-gray-200 shadow-xl">
      {/* Title bar */}
      <div className="px-4 py-3 border-b flex items-center justify-between bg-gradient-to-br from-purple-50 to-white">
        <div className="flex items-center gap-2">
          <span className="text-purple-600 text-lg leading-none">✦</span>
          <div>
            <div className="text-sm font-semibold text-gray-800">Ada</div>
            <div className="text-[11px] text-gray-500">Read-only assistant</div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-gray-400 hover:text-gray-600 text-lg leading-none p-1"
          title="Close"
        >
          ×
        </button>
      </div>

      {/* Conversation menu */}
      <div className="px-3 py-2 border-b flex items-center gap-2 bg-white">
        <div className="relative flex-1 min-w-0">
          <button
            type="button"
            onClick={() => setMenuOpen((m) => !m)}
            disabled={!inScope}
            className="w-full text-left text-xs px-2 py-1 border border-gray-200 rounded hover:bg-gray-50 flex items-center gap-1 disabled:opacity-50"
          >
            <span className="flex-1 truncate text-gray-700">
              {activeConv?.title || 'No conversation'}
            </span>
            <span className="text-gray-400">▾</span>
          </button>
          {menuOpen && (() => {
            // Filter by search, then bucket by age. Buckets render in a fixed
            // order (most-recent first) so the user's eye can land quickly.
            const q = menuSearch.trim().toLowerCase();
            const filtered = q
              ? conversations.filter((c) => (c.title || '').toLowerCase().includes(q))
              : conversations;
            const grouped: Record<TimeBucket, Conversation[]> = {
              Today: [], 'This week': [], 'This month': [], Older: [],
            };
            for (const c of filtered) {
              grouped[bucketByAge(c.last_message_at || c.updated_at || c.created_at)].push(c);
            }
            return (
              <div className="absolute left-0 right-0 mt-1 max-h-80 overflow-y-auto bg-white border border-gray-200 rounded shadow-lg z-10">
                {/* Title search — client-side filter, instant. */}
                <div className="sticky top-0 bg-white border-b border-gray-100 p-1.5">
                  <input
                    type="text"
                    value={menuSearch}
                    onChange={(e) => setMenuSearch(e.target.value)}
                    placeholder="Search chats…"
                    autoFocus
                    className="w-full text-xs px-2 py-1 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-purple-400"
                  />
                </div>

                {filtered.length === 0 && (
                  <div className="px-3 py-3 text-xs text-gray-400">
                    {q ? `No chats matching "${menuSearch}".` : 'No saved chats yet.'}
                  </div>
                )}

                {TIME_BUCKETS.map((bucket) => {
                  const rows = grouped[bucket];
                  if (rows.length === 0) return null;
                  return (
                    <div key={bucket}>
                      <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-gray-400">
                        {bucket}
                      </div>
                      {rows.map((c) => (
                        <div
                          key={c.id}
                          className={`group w-full px-3 py-2 text-xs hover:bg-gray-50 flex items-center gap-1.5 ${
                            c.id === activeConversationId ? 'bg-purple-50' : ''
                          }`}
                        >
                          {renamingId === c.id ? (
                            <input
                              type="text"
                              value={renamingDraft}
                              autoFocus
                              onChange={(e) => setRenamingDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') commitRename(c.id);
                                else if (e.key === 'Escape') cancelRenaming();
                              }}
                              onBlur={() => commitRename(c.id)}
                              onClick={(e) => e.stopPropagation()}
                              className="flex-1 min-w-0 px-1 py-0.5 text-xs border border-purple-400 rounded focus:outline-none"
                            />
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleSwitchConversation(c.id)}
                              className="flex-1 min-w-0 text-left truncate"
                              title={c.title}
                            >
                              {c.title}
                            </button>
                          )}
                          {renamingId !== c.id && (
                            <>
                              <span
                                onClick={(e) => startRenaming(c, e)}
                                className="text-gray-300 group-hover:text-gray-500 hover:text-gray-700 cursor-pointer text-[10px]"
                                title="Rename"
                              >
                                ✎
                              </span>
                              <span
                                onClick={(e) => handleDeleteConversation(c.id, e)}
                                className="text-gray-300 group-hover:text-gray-500 hover:text-red-500 cursor-pointer"
                                title="Delete"
                              >
                                ×
                              </span>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
        <button
          type="button"
          onClick={handleNewChat}
          disabled={!inScope}
          className="text-xs px-2 py-1 rounded bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50"
          title="Start a new chat"
        >
          + New
        </button>
      </div>

      {/* Live context indicator */}
      <div className="px-4 py-2 text-[11px] text-gray-500 border-b bg-gray-50">
        {inScope ? (
          <>
            Asking about <span className="font-medium text-gray-800">{ctx.dataType}</span>
            {ctx.projectName && <> in <span className="font-medium text-gray-800">{ctx.projectName}</span></>}
            {ctx.associatedCompany && <> at <span className="font-medium text-gray-800">{ctx.associatedCompany}</span></>}
            {ctx.searchTerm && <> · search: <span className="font-medium text-gray-800">"{ctx.searchTerm}"</span></>}
            {ctx.visibleTotal != null && !ctx.associatedCompany && <> · {ctx.visibleTotal.toLocaleString()} rows in scope</>}
            {ctx.filterGroups && ctx.filterGroups.length > 0 && <> · filters active</>}
            {ctx.employmentTypes && ctx.employmentTypes.length > 0 && <> · emp-type filter</>}
            {ctx.radius && Object.keys(ctx.radius).length > 0 && <> · radius active</>}
          </>
        ) : (
          <>Open a project's data tab to start asking questions.</>
        )}
      </div>

      {/* Thread */}
      <div ref={threadRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {loadingConversation && (
          <div className="text-xs text-gray-400">Loading chat…</div>
        )}
        {!loadingConversation && messages.length === 0 && inScope && (
          <div className="text-sm text-gray-500">
            <div className="mb-3 text-gray-700 font-medium">Good {timeOfDay()}. Try asking me to:</div>
            {(['analyse', 'navigate'] as const).map((kind) => {
              const ideas = suggestionsFor(ctx.dataType, kind);
              if (ideas.length === 0) return null;
              const heading = kind === 'analyse' ? '📊 Analyse this data' : '⚙️ Filter or navigate';
              return (
                <div key={kind} className="mb-3">
                  <div className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">{heading}</div>
                  <ul className="space-y-1">
                    {ideas.map((s, i) => (
                      <li key={i}>
                        <button
                          className="text-left text-xs px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 w-full text-gray-700"
                          onClick={() => setInput(s)}
                        >
                          {s}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}

        {messages.map((m, i) => {
          const prevUserCtx = messages.slice(0, i).reverse().find((p) => p.role === 'user')?.context;
          const showDivider =
            m.role === 'user' && m.context && prevUserCtx && !sameContext(prevUserCtx, m.context);
          return (
            <React.Fragment key={i}>
              {showDivider && m.context && (
                <div className="my-2 flex items-center gap-2 text-[10px] text-gray-400">
                  <span className="flex-1 border-t border-gray-200" />
                  <span>Context: {describeContext(m.context)}</span>
                  <span className="flex-1 border-t border-gray-200" />
                </div>
              )}
              <MessageBubble
                message={m}
                renderToolResult={renderToolResult}
                onApplyActions={() => applyPendingActions(i)}
                onDiscardActions={() => discardPendingActions(i)}
              />
            </React.Fragment>
          );
        })}

        {streaming && (
          <div className="text-xs text-gray-400 flex items-center gap-1">
            <span className="inline-block w-1 h-1 bg-gray-400 rounded-full animate-pulse" />
            <span className="inline-block w-1 h-1 bg-gray-400 rounded-full animate-pulse [animation-delay:150ms]" />
            <span className="inline-block w-1 h-1 bg-gray-400 rounded-full animate-pulse [animation-delay:300ms]" />
          </div>
        )}
      </div>

      {/* Input */}
      <div className="border-t p-3 bg-white">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          rows={2}
          placeholder={inScope ? 'Ask anything about this data...' : 'Open a project tab first'}
          disabled={!inScope || streaming}
          className="w-full text-sm border border-gray-200 rounded-md px-2 py-1.5 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-400"
        />
        <div className="mt-1 flex items-center justify-between">
          <div className="text-[10px] text-gray-400">Powered by Claude · read-only</div>
          <button
            type="button"
            onClick={send}
            disabled={!input.trim() || !inScope || streaming}
            className="text-xs px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Send
          </button>
        </div>
      </div>
    </aside>
  );
}

// Tiny inline renderer for **bold** and *italic* — Claude uses these in its
// thematic summaries (e.g. "**Autonomous Driving** — ... *Sam Smith*").
// React handles escaping so this can't introduce XSS.
function renderInline(text: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const pattern = /(\*\*[^*\n]+\*\*|\*[^*\n]+\*)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      out.push(text.slice(lastIndex, match.index));
    }
    const tok = match[0];
    if (tok.startsWith('**')) {
      out.push(<strong key={key++}>{tok.slice(2, -2)}</strong>);
    } else {
      out.push(<em key={key++}>{tok.slice(1, -1)}</em>);
    }
    lastIndex = match.index + tok.length;
  }
  if (lastIndex < text.length) out.push(text.slice(lastIndex));
  return out;
}

function MessageBubble({
  message,
  renderToolResult,
  onApplyActions,
  onDiscardActions,
}: {
  message: Message;
  renderToolResult: (toolName: string, result: any) => React.ReactNode;
  onApplyActions?: () => void;
  onDiscardActions?: () => void;
}) {
  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="bg-purple-600 text-white text-sm px-3 py-1.5 rounded-lg rounded-br-sm max-w-[85%] whitespace-pre-wrap break-words">
          {message.content}
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {/* Claude's pre-text shown above the action card when actions are pending */}
      {message.pendingActions && message.pendingActions.preText && (
        <div className="text-sm text-gray-800 whitespace-pre-wrap break-words">
          {renderInline(message.pendingActions.preText)}
        </div>
      )}
      {message.pendingActions && (
        <ActionCard
          pending={message.pendingActions}
          onApply={onApplyActions}
          onDiscard={onDiscardActions}
        />
      )}
      {message.toolUses.map((tu) => (
        <ToolCard
          key={tu.id}
          toolUse={tu}
          result={message.toolResults[tu.id]}
          renderToolResult={renderToolResult}
        />
      ))}
      {message.content && (
        <div className="text-sm text-gray-800 whitespace-pre-wrap break-words">
          {renderInline(message.content)}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ActionCard — shown when Claude proposes one or more action tools that
// haven't been approved yet. Stages all proposed actions together (one Apply
// button per proposal). Once applied or discarded, collapses to a tiny
// confirmation row that the user can ignore.
// ---------------------------------------------------------------------------

// Human-readable operator label. Used in filter-condition previews so the
// user actually understands what's being applied (instead of seeing the
// raw "starts_with" / "greater_equal" enum names).
function operatorLabel(op: string): string {
  switch (op) {
    case 'contains': return 'contains';
    case 'not_contains': return 'does not contain';
    case 'equals': return '=';
    case 'not_equals': return '≠';
    case 'starts_with': return 'starts with';
    case 'ends_with': return 'ends with';
    case 'greater_than': return '>';
    case 'less_than': return '<';
    case 'greater_equal': return '≥';
    case 'less_equal': return '≤';
    case 'is_empty': return 'is empty';
    case 'is_not_empty': return 'is not empty';
    default: return op;
  }
}

function entityLabel(entityType: string | undefined): string {
  if (entityType === 'candidate') return 'candidates';
  if (entityType === 'company') return 'companies';
  if (entityType === 'manager') return 'managers';
  return 'rows';
}

function describeAction(tu: ToolUse): React.ReactNode {
  const input = tu.input || {};
  if (tu.name === 'apply_filter_groups') {
    const groups: any[] = input.groups || [];
    const mode = input.mode || 'replace';
    const verb = mode === 'add' ? 'Add' : 'Apply';
    return (
      <div>
        <div>
          <span className="text-gray-500">
            {verb} filter{groups.length === 1 ? '' : ` (${groups.length} groups)`}:
          </span>
        </div>
        {groups.map((g, gi) => {
          const conds: any[] = Array.isArray(g.conditions) ? g.conditions : [];
          const logic = g.logic || 'AND';
          return (
            <div key={gi} className="mt-0.5 pl-2 border-l-2 border-amber-200">
              <div className="text-[10px] uppercase tracking-wide text-gray-400">
                {entityLabel(g.entityType)}
                {g.name ? ` · ${g.name}` : ''}
                {conds.length > 1 ? ` · ${logic}` : ''}
              </div>
              {conds.map((c, ci) => {
                const op = operatorLabel(c.operator || '');
                const vals: any[] = Array.isArray(c.values) && c.values.length > 0
                  ? c.values
                  : (c.value != null && c.value !== '' ? [c.value] : []);
                const valStr = vals.length > 0
                  ? vals.map(v => `"${v}"`).join(' AND ')
                  : '';
                return (
                  <div key={ci} className="text-gray-700">
                    <code className="text-[11px] bg-amber-100/60 px-1 rounded">{c.column}</code>{' '}
                    <span className="text-gray-500">{op}</span>
                    {valStr && <> <strong>{valStr}</strong></>}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  }
  if (tu.name === 'switch_tab') {
    return <span><span className="text-gray-500">Switch to</span> <strong>{input.tab}</strong> <span className="text-gray-500">tab</span></span>;
  }
  if (tu.name === 'apply_radius_search') {
    return (
      <span>
        <span className="text-gray-500">Radius search:</span>{' '}
        <strong>{input.distance} miles</strong> <span className="text-gray-500">around</span>{' '}
        <strong>{input.location}</strong> <span className="text-gray-500">({input.type} location)</span>
      </span>
    );
  }
  if (tu.name === 'set_keyword_search') {
    if (!input.term) return <span><span className="text-gray-500">Clear the keyword search bar</span></span>;
    return <span><span className="text-gray-500">Set keyword search to</span> <strong>"{input.term}"</strong></span>;
  }
  if (tu.name === 'propose_smart_search') {
    return (
      <span>
        <span className="text-gray-500">Open AI Smart Search preview for</span>{' '}
        <strong>"{input.query}"</strong>{' '}
        <span className="text-gray-500">(you can tweak keywords + columns before applying)</span>
      </span>
    );
  }
  if (tu.name === 'set_employment_types') {
    const types: string[] = Array.isArray(input.types) ? input.types : [];
    if (types.length === 0) return <span className="text-gray-500">Clear the Employment Type picker</span>;
    return (
      <span>
        <span className="text-gray-500">Set Employment Type picker to:</span>{' '}
        <strong>{types.join(', ')}</strong>
      </span>
    );
  }
  return <span className="text-gray-500 font-mono">{tu.name}</span>;
}

// Status pip rendered next to each step. Visually narrates the queued →
// running → done sequence so the user perceives Ada actually performing
// the actions one by one, rather than everything happening at once.
function StepIndicator({ status }: { status: StepStatus }) {
  if (status === 'queued') {
    return (
      <span
        className="inline-block w-2.5 h-2.5 rounded-full border border-gray-300 bg-white"
        title="Queued"
      />
    );
  }
  if (status === 'running') {
    return (
      <span
        className="inline-block w-2.5 h-2.5 rounded-full border-2 border-amber-500 border-t-transparent animate-spin"
        title="Running"
      />
    );
  }
  if (status === 'done') {
    return (
      <span className="text-emerald-600 text-xs leading-none" title="Done">
        ✓
      </span>
    );
  }
  return (
    <span className="text-red-600 text-xs leading-none" title="Error">
      ✕
    </span>
  );
}

function ActionCard({
  pending,
  onApply,
  onDiscard,
}: {
  pending: PendingActions;
  onApply?: () => void;
  onDiscard?: () => void;
}) {
  const { toolUses, status, stepStatuses, errors } = pending;

  if (status === 'applied' || status === 'discarded') {
    const verbed = status === 'applied' ? 'Applied' : 'Discarded';
    const colour = status === 'applied' ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-gray-50';
    const tickColour = status === 'applied' ? 'text-emerald-700' : 'text-gray-500';
    return (
      <div className={`text-[11px] border rounded px-2 py-1.5 ${colour}`}>
        <div className={`flex items-center gap-1.5 font-medium ${tickColour}`}>
          <span>{status === 'applied' ? '✓' : '−'}</span>
          <span>{verbed}</span>
        </div>
        {/* Show what was applied so the user can see + learn what Ada did. */}
        <ul className="mt-1 space-y-0.5 pl-4">
          {toolUses.map((tu) => {
            const st = stepStatuses?.[tu.id];
            const err = errors?.[tu.id];
            return (
              <li key={tu.id} className="text-gray-700 flex items-start gap-1.5">
                {status === 'applied' && st && (
                  <span className="mt-0.5 flex-shrink-0"><StepIndicator status={st} /></span>
                )}
                <span className="flex-1">
                  {describeAction(tu)}
                  {err && <span className="ml-1 text-red-600">— {err}</span>}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  const applying = status === 'applying';

  return (
    <div className="border-2 border-amber-300 bg-amber-50/40 rounded-lg p-3 space-y-2">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-900">
        <span>✨</span>
        <span>{applying ? 'Performing actions…' : `Suggested action${toolUses.length === 1 ? '' : 's'}`}</span>
      </div>
      <ul className="space-y-1.5 text-xs text-gray-800">
        {toolUses.map((tu) => {
          const st = stepStatuses?.[tu.id];
          const err = errors?.[tu.id];
          return (
            <li key={tu.id} className="flex items-start gap-1.5">
              {applying && st ? (
                <span className="mt-0.5 flex-shrink-0 w-3 flex justify-center">
                  <StepIndicator status={st} />
                </span>
              ) : (
                <span className="text-amber-600 mt-0.5">›</span>
              )}
              <span
                className={`flex-1 ${st === 'running' ? 'text-amber-900 font-medium' : ''} ${
                  st === 'queued' ? 'text-gray-500' : ''
                }`}
              >
                {describeAction(tu)}
                {err && <span className="ml-1 text-red-600">— {err}</span>}
              </span>
            </li>
          );
        })}
      </ul>
      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={onApply}
          disabled={applying}
          className="text-xs px-3 py-1 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
        >
          {applying ? 'Applying…' : `✓ Apply${toolUses.length > 1 ? ' all' : ''}`}
        </button>
        <button
          type="button"
          onClick={onDiscard}
          disabled={applying}
          className="text-xs px-3 py-1 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          ✕ Discard
        </button>
      </div>
    </div>
  );
}

function ToolCard({
  toolUse,
  result,
  renderToolResult,
}: {
  toolUse: ToolUse;
  result?: ToolResult;
  renderToolResult: (toolName: string, result: any) => React.ReactNode;
}) {
  const [expanded, setExpanded] = useState(true);
  const pending = !result;
  const inputEntries = Object.entries(toolUse.input || {});
  const inputCollapsedLine = inputEntries
    .map(([k, v]) => `${k}=${JSON.stringify(v)}`)
    .join(', ');
  const isWebSearch = toolUse.name === 'web_search';
  return (
    <div className={`border rounded ${result?.is_error ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'}`}>
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="w-full text-left px-2 py-1 text-[11px] text-gray-600 flex items-start gap-1 hover:bg-gray-100"
        title={inputCollapsedLine}
      >
        <span className="text-purple-600 leading-tight">{isWebSearch ? '🌐' : '🔧'}</span>
        <span className="font-mono leading-tight">
          {isWebSearch ? 'Searched the web' : toolUse.name}
        </span>
        {!expanded && isWebSearch && toolUse.input?.query && (
          <span className="text-gray-500 truncate flex-1 leading-tight italic">
            "{toolUse.input.query}"
          </span>
        )}
        {!expanded && !isWebSearch && (
          <span className="text-gray-400 truncate flex-1 leading-tight">({inputCollapsedLine})</span>
        )}
        {expanded && <span className="flex-1" />}
        {pending ? (
          <span className="text-gray-400 leading-tight">running…</span>
        ) : (
          <span className="text-gray-400 leading-tight">{expanded ? '▾' : '▸'}</span>
        )}
      </button>
      {expanded && (
        <div className="px-2 pb-2">
          {inputEntries.length > 0 && (
            <div className="mb-2 text-[10px] text-gray-500 font-mono">
              {inputEntries.map(([k, v]) => (
                <div key={k} className="break-all">
                  <span className="text-gray-400">{k}</span>={JSON.stringify(v)}
                </div>
              ))}
            </div>
          )}
          {result && renderToolResult(toolUse.name, result.result)}
        </div>
      )}
    </div>
  );
}

function timeOfDay() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 18) return 'afternoon';
  return 'evening';
}

function suggestionsFor(
  dataType: AdaContextValue['dataType'],
  kind: 'analyse' | 'navigate'
): string[] {
  if (kind === 'navigate') {
    if (dataType === 'companies') {
      return [
        'Filter to software companies with 30–60 employees',
        'Switch to candidates tab',
        'Show me companies in pharma',
      ];
    }
    if (dataType === 'candidates') {
      return [
        'Find Databricks engineers near Manchester',
        'Filter candidates whose current role is "VP Engineering"',
        'Search for "AWS"',
      ];
    }
    if (dataType === 'managers') {
      return [
        'Find heads of talent at companies in pharma',
        'Filter managers tagged "priority"',
        'Show managers in London',
      ];
    }
    return [];
  }
  // analyse — the existing data-tool prompts
  if (dataType === 'companies') {
    return [
      'What are the top 10 subindustries here?',
      'Which business types are most common?',
      'How are these companies distributed by industry?',
    ];
  }
  if (dataType === 'candidates') {
    return [
      'Top 10 current companies for these candidates',
      'Most common job titles in this view',
      'Where did most of these candidates work previously?',
    ];
  }
  if (dataType === 'managers') {
    return [
      'Top 10 companies for these hiring managers',
      'Most common job titles among managers here',
      'Which seniority levels are most represented?',
    ];
  }
  return [];
}
